import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  approveReplacement,
  getReplacement,
  listReplacementCategories,
  listReplacementCities,
  rejectReplacement,
} from './api';
import type { ApproveReplacementBody, RejectReplacementValues } from './types';

/** Query-key factory for the replacements feature. */
export const replacementKeys = {
  all: ['replacements'] as const,
  detail: (id: string) => [...replacementKeys.all, 'detail', id] as const,
  metaCities: () => [...replacementKeys.all, 'meta', 'cities'] as const,
  metaCategories: () => [...replacementKeys.all, 'meta', 'categories'] as const,
  /** Cursor typeaheads keyed by search term so a new term starts a fresh first page. */
  metaCustomers: (search: string) => [...replacementKeys.all, 'meta', 'customers', search] as const,
  metaDevices: (search: string) => [...replacementKeys.all, 'meta', 'devices', search] as const,
};

/** Single replacement-request detail; idle while `id` is empty (drawer closed). */
export function useReplacement(id: string) {
  return useQuery({
    queryKey: replacementKeys.detail(id),
    queryFn: () => getReplacement(id),
    enabled: Boolean(id),
  });
}

/** Rejects the replacement request with an admin-entered reason. The real backend's reject
 *  response is slim (`{id, status}`), so this invalidates rather than seeds the detail cache —
 *  row + drawer both refetch and show Rejected. */
export function useRejectReplacement(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (values: RejectReplacementValues) => rejectReplacement(id, values),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: replacementKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: replacementKeys.all });
    },
  });
}

/** Approves the replacement request (with or without an installation request). The real
 *  backend's approve response is slim, so this invalidates rather than seeds the detail cache —
 *  row + drawer both refetch and show Approved. */
export function useApproveReplacement(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ApproveReplacementBody) => approveReplacement(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: replacementKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: replacementKeys.all });
    },
  });
}

/** City filter options; cached 5 min (changes rarely). */
export function useReplacementCities() {
  return useQuery({
    queryKey: replacementKeys.metaCities(),
    queryFn: listReplacementCities,
    staleTime: 5 * 60 * 1000,
  });
}

/** Device Category filter options; cached 5 min. */
export function useReplacementCategories() {
  return useQuery({
    queryKey: replacementKeys.metaCategories(),
    queryFn: listReplacementCategories,
    staleTime: 5 * 60 * 1000,
  });
}
